import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ProjectManagementService } from '../project-management.service';
import { DocumentCreateDialogComponent } from './document-create-dialog/document-create-dialog.component';

interface DocumentRecord {
  id: string;
  title: string;
  type: string;
  contentType?: string | null;
  source: string;
  uploadedBy: string;
  uploadedAt: string | null;
  url: string;
  fileName: string;
}

interface KnowledgeFolder {
  id: number;
  projectId: number;
  parentFolderId?: number | null;
  name: string;
  description?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface KnowledgeDocument {
  id: number;
  projectId: number;
  folderId?: number | null;
  title: string;
  documentType: string;
  summary?: string | null;
  content?: string | null;
  status: string;
  createdByName?: string | null;
  updatedByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-project-documents',
  templateUrl: './project-documents.component.html',
  styleUrls: ['./project-documents.component.scss']
})
export class ProjectDocumentsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('documentEditor') documentEditor?: ElementRef<HTMLDivElement>;
  private resizeState: {
    cell: HTMLElement;
    directionX: 'left' | 'right' | null;
    directionY: 'top' | 'bottom' | null;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null = null;
  private readonly editorPointerMoveHandler = (event: PointerEvent) => this.handleEditorPointerMove(event);
  private readonly editorPointerDownHandler = (event: PointerEvent) => this.handleEditorPointerDown(event);
  private readonly globalPointerMoveHandler = (event: PointerEvent) => this.handleGlobalPointerMove(event);
  private readonly globalPointerUpHandler = () => this.stopResize();

  loading = false;
  workspaceLoading = false;
  savingWorkspace = false;
  projects: any[] = [];
  selectedProjectId: number | null = null;
  selectedProject: any = null;
  tasks: any[] = [];
  onboarding: any[] = [];
  selectedDocument: DocumentRecord | null = null;
  selectedDocumentId: string | null = null;
  pdfPreviewUrl: SafeResourceUrl | null = null;
  search = '';

  viewMode: 'uploads' | 'workspace' = 'uploads';
  workspaceSearch = '';
  currentFolderId: number | null = null;
  expandedFolderIds = new Set<number>();
  folders: KnowledgeFolder[] = [];
  knowledgeDocuments: KnowledgeDocument[] = [];
  selectedKnowledgeDocumentId: number | null = null;
  selectedKnowledgeDocument: KnowledgeDocument | null = null;
  editorModel = {
    title: '',
    documentType: 'Document',
    content: '',
    status: 'Draft',
    folderId: null as number | null
  };

  constructor(
    private projectService: ProjectManagementService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  ngAfterViewInit(): void {
    this.syncEditorSurface();
    this.bindEditorInteractions();
  }

  ngOnDestroy(): void {
    this.unbindEditorInteractions();
  }

  loadProjects(): void {
    this.loading = true;
    this.projectService.getProjects().subscribe({
      next: (projects) => {
        this.projects = projects || [];
        this.selectedProjectId = this.projects[0]?.id || null;
        this.loadDocuments();
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load projects.', 'error');
      }
    });
  }

  loadDocuments(): void {
    if (!this.selectedProjectId) {
      this.selectedProject = null;
      this.tasks = [];
      this.onboarding = [];
      this.folders = [];
      this.knowledgeDocuments = [];
      this.selectedDocument = null;
      this.selectedKnowledgeDocument = null;
      this.loading = false;
      return;
    }

    this.loading = true;
    forkJoin({
      project: this.projectService.getProject(this.selectedProjectId),
      tasks: this.projectService.getProjectTasks(this.selectedProjectId),
      onboarding: this.projectService.getProjectOnboarding(this.selectedProjectId),
      knowledge: this.projectService.getProjectKnowledgeWorkspace(this.selectedProjectId)
    }).subscribe({
      next: ({ project, tasks, onboarding, knowledge }) => {
        this.selectedProject = project || null;
        this.tasks = tasks || [];
        this.onboarding = onboarding || [];
        this.folders = knowledge?.folders || [];
        this.knowledgeDocuments = knowledge?.documents || [];
        this.selectedDocumentId = null;
        this.currentFolderId = null;
        this.ensureSelectedDocument();
        this.ensureSelectedKnowledgeDocument();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load project documents.', 'error');
      }
    });
  }

  get documents(): DocumentRecord[] {
    const projectDocs = (this.selectedProject?.mediaAttachments || []).map((file: any, index: number) => ({
      id: `project-${index}`,
      title: file.fileName || file.name || `Project file ${index + 1}`,
      type: this.getFileTypeLabel(file),
      contentType: file.contentType || null,
      source: 'Project',
      uploadedBy: this.selectedProject?.createdByName || this.selectedProject?.leadStaffName || 'System',
      uploadedAt: file.uploadedAt || this.selectedProject?.updatedAt || this.selectedProject?.createdAt || null,
      url: file.url || file.fileUrl || '',
      fileName: file.fileName || file.name || 'Attachment'
    }));

    const taskDocs = this.tasks.reduce((records: DocumentRecord[], task) => {
      const attachments = (task?.mediaAttachments || []).map((file: any, index: number) => ({
        id: `task-${task.id}-${index}`,
        title: file.fileName || file.name || task.title,
        type: this.getFileTypeLabel(file),
        contentType: file.contentType || null,
        source: `Task: ${task.title}`,
        uploadedBy: task.assigneeName || task.ownerName || 'Project Team',
        uploadedAt: file.uploadedAt || task.updatedAt || task.createdAt || null,
        url: file.url || file.fileUrl || '',
        fileName: file.fileName || file.name || 'Attachment'
      }));
      return [...records, ...attachments];
    }, []);

    const onboardingDocs = this.onboarding.reduce((records: DocumentRecord[], item) => {
      const attachmentsSource = (item?.mediaAttachments?.length
        ? item.mediaAttachments
        : item?.attachmentUrl
          ? [{
              fileName: item.attachmentName || item.title || 'Attachment',
              url: item.attachmentUrl,
              contentType: null,
              uploadedAt: item.updatedAt || item.uploadedAt || item.createdAt || null
            }]
          : []);

      const attachments = attachmentsSource.map((file: any, index: number) => ({
        id: `onboarding-${item.id}-${index}`,
        title: file.fileName || file.name || item.title,
        type: this.getFileTypeLabel(file),
        contentType: file.contentType || null,
        source: `Onboarding: ${item.title}`,
        uploadedBy: item.uploadedByName || item.assigneeName || item.ownerName || 'Project Desk',
        uploadedAt: file.uploadedAt || item.updatedAt || item.uploadedAt || item.createdAt || null,
        url: file.url || file.fileUrl || '',
        fileName: file.fileName || file.name || item.attachmentName || 'Attachment'
      }));
      return [...records, ...attachments];
    }, []);

    return [...projectDocs, ...taskDocs, ...onboardingDocs].filter((doc) => !!doc.url);
  }

  get filteredDocuments(): DocumentRecord[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.documents;
    return this.documents.filter((doc) =>
      [doc.title, doc.type, doc.source, doc.uploadedBy, doc.fileName].some((value) =>
        String(value || '').toLowerCase().includes(term)
      )
    );
  }

  get displayedDocument(): DocumentRecord | null {
    return this.filteredDocuments.find((doc) => doc.id === this.selectedDocumentId)
      || this.documents.find((doc) => doc.id === this.selectedDocumentId)
      || null;
  }

  get typeSummary(): Array<{ label: string; value: number }> {
    const imageCount = this.documents.filter((doc) => doc.type === 'Image').length;
    const pdfCount = this.documents.filter((doc) => doc.type === 'PDF').length;
    const mediaCount = this.documents.filter((doc) => ['Video', 'Audio'].includes(doc.type)).length;
    const otherCount = this.documents.filter((doc) => !['Image', 'PDF', 'Video', 'Audio'].includes(doc.type)).length;
    return [
      { label: 'Images', value: imageCount },
      { label: 'PDFs', value: pdfCount },
      { label: 'Media', value: mediaCount },
      { label: 'Other Files', value: otherCount }
    ];
  }

  get rootFolderCount(): number {
    return this.folders.filter((folder) => !folder.parentFolderId).length;
  }

  get workspaceDocumentCount(): number {
    return this.knowledgeDocuments.length;
  }

  get currentFolder(): KnowledgeFolder | null {
    return this.folders.find((folder) => folder.id === this.currentFolderId) || null;
  }

  get folderBreadcrumbs(): KnowledgeFolder[] {
    const trail: KnowledgeFolder[] = [];
    let cursor = this.currentFolder;
    while (cursor) {
      trail.unshift(cursor);
      cursor = this.folders.find((folder) => folder.id === cursor?.parentFolderId) || null;
    }
    return trail;
  }

  get visibleFolders(): KnowledgeFolder[] {
    const term = this.workspaceSearch.trim().toLowerCase();
    return this.folders
      .filter((folder) => (folder.parentFolderId || null) === this.currentFolderId)
      .filter((folder) => !term || [folder.name].some((value) => String(value || '').toLowerCase().includes(term)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get visibleKnowledgeDocuments(): KnowledgeDocument[] {
    const term = this.workspaceSearch.trim().toLowerCase();
    return this.knowledgeDocuments
      .filter((document) => (document.folderId || null) === this.currentFolderId)
      .filter((document) => !term || [document.title, document.content, document.status, document.updatedByName].some((value) => String(value || '').toLowerCase().includes(term)))
      .sort((a, b) => (new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
  }

  get activeKnowledgeDocument(): KnowledgeDocument | null {
    return this.knowledgeDocuments.find((document) => document.id === this.selectedKnowledgeDocumentId) || null;
  }

  openPreview(document: DocumentRecord): void {
    this.selectedDocumentId = document.id;
    this.selectedDocument = document;
    this.pdfPreviewUrl = this.mediaKind(document) === 'pdf'
      ? this.sanitizer.bypassSecurityTrustResourceUrl(document.url)
      : null;
  }

  onSearchChange(): void {
    this.ensureSelectedDocument();
  }

  mediaKind(document: DocumentRecord | null): 'image' | 'video' | 'audio' | 'pdf' | 'file' {
    const contentType = String(document?.contentType || '').toLowerCase();
    const fileName = String(document?.fileName || document?.title || '').toLowerCase();
    const url = String(document?.url || '').toLowerCase();

    if (contentType.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(fileName) || url.startsWith('data:image/')) {
      return 'image';
    }
    if (contentType.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/i.test(fileName) || url.startsWith('data:video/')) {
      return 'video';
    }
    if (contentType.startsWith('audio/') || /\.(mp3|wav|ogg|m4a)$/i.test(fileName) || url.startsWith('data:audio/')) {
      return 'audio';
    }
    if (contentType === 'application/pdf' || /\.pdf$/i.test(fileName) || url.startsWith('data:application/pdf')) {
      return 'pdf';
    }

    return 'file';
  }

  openFolder(folder: KnowledgeFolder): void {
    this.currentFolderId = folder.id;
    this.selectedKnowledgeDocumentId = null;
    this.ensureSelectedKnowledgeDocument();
  }

  navigateToRoot(): void {
    this.currentFolderId = null;
    this.ensureSelectedKnowledgeDocument();
  }

  navigateToFolder(folderId: number | null): void {
    this.currentFolderId = folderId;
    this.ensureSelectedKnowledgeDocument();
  }

  openKnowledgeDocument(document: KnowledgeDocument): void {
    this.selectedKnowledgeDocumentId = document.id;
    this.selectedKnowledgeDocument = document;
    this.editorModel = {
      title: document.title || '',
      documentType: document.documentType || 'Document',
      content: document.content || '',
      status: document.status || 'Draft',
      folderId: document.folderId ?? null
    };
    this.syncEditorSurface();
  }

  onEditorInput(): void {
    this.editorModel.content = this.documentEditor?.nativeElement.innerHTML || '';
  }

  applyEditorCommand(command: string, value?: string): void {
    this.focusEditor();
    document.execCommand(command, false, value);
    this.onEditorInput();
  }

  applyBlock(block: 'P' | 'H1' | 'H2' | 'BLOCKQUOTE'): void {
    const tag = block === 'P' ? 'p' : block === 'H1' ? 'h1' : block === 'H2' ? 'h2' : 'blockquote';
    this.applyEditorCommand('formatBlock', tag);
  }

  clearEditorFormatting(): void {
    this.focusEditor();
    document.execCommand('removeFormat', false);
    document.execCommand('formatBlock', false, 'p');
    this.onEditorInput();
  }

  deleteSelectedTable(): void {
    const table = this.getSelectedTableCell()?.closest('table');

    if (!table) {
      Swal.fire('No table selected', 'Place your cursor inside a table before deleting it.', 'info');
      return;
    }

    table.remove();
    this.onEditorInput();
  }

  addTableRow(): void {
    const cell = this.getSelectedTableCell();
    if (!cell) {
      Swal.fire('No table cell selected', 'Place your cursor inside a table cell first.', 'info');
      return;
    }

    const row = cell.closest('tr') as HTMLTableRowElement | null;
    if (!row) return;

    const columnCount = row.cells.length || 1;
    const newRow = row.insertAdjacentElement('afterend', document.createElement('tr')) as HTMLTableRowElement | null;
    const targetRow = newRow || (row.nextElementSibling as HTMLTableRowElement | null);
    if (!targetRow) return;

    targetRow.innerHTML = Array.from({ length: columnCount }, (_, index) =>
      `<td style="${this.getBodyCellStyle()}"><div class="doc-table-cell" style="${this.getCellContentStyle()}" contenteditable="true">New Row, Col ${index + 1}</div></td>`
    ).join('');

    this.onEditorInput();
  }

  addTableColumn(): void {
    const cell = this.getSelectedTableCell();
    if (!cell) {
      Swal.fire('No table cell selected', 'Place your cursor inside a table cell first.', 'info');
      return;
    }

    const table = cell.closest('table');
    const row = cell.closest('tr') as HTMLTableRowElement | null;
    if (!table || !row) return;

    const cellIndex = Array.from(row.cells).indexOf(cell as HTMLTableCellElement);
    table.querySelectorAll('tr').forEach((tableRow) => {
      const currentRow = tableRow as HTMLTableRowElement;
      const isHeaderRow = currentRow.parentElement?.tagName === 'THEAD' || currentRow.querySelector('th');
      const insertionIndex = Math.max(0, cellIndex + 1);
      const newCell = isHeaderRow
        ? document.createElement('th')
        : document.createElement('td');

      newCell.setAttribute('style', isHeaderRow ? this.getHeaderCellStyle() : this.getBodyCellStyle());
      newCell.innerHTML = `<div class="doc-table-cell" style="${this.getCellContentStyle()}" contenteditable="true">${isHeaderRow ? 'New Header' : 'New Cell'}</div>`;

      const referenceCell = currentRow.cells[insertionIndex] || null;
      currentRow.insertBefore(newCell, referenceCell);
    });

    this.onEditorInput();
  }

  deleteTableRow(): void {
    const cell = this.getSelectedTableCell();
    if (!cell) {
      Swal.fire('No table cell selected', 'Place your cursor inside a table cell first.', 'info');
      return;
    }

    const row = cell.closest('tr') as HTMLTableRowElement | null;
    const table = cell.closest('table');
    if (!row || !table) return;

    const rows = table.querySelectorAll('tr');
    if (rows.length <= 1) {
      Swal.fire('Cannot delete row', 'A table must keep at least one row.', 'info');
      return;
    }

    row.remove();
    this.onEditorInput();
  }

  deleteTableColumn(): void {
    const cell = this.getSelectedTableCell();
    if (!cell) {
      Swal.fire('No table cell selected', 'Place your cursor inside a table cell first.', 'info');
      return;
    }

    const table = cell.closest('table');
    const row = cell.closest('tr') as HTMLTableRowElement | null;
    if (!table || !row) return;

    const cellIndex = Array.from(row.cells).indexOf(cell as HTMLTableCellElement);
    const columnCount = row.cells.length;
    if (columnCount <= 1) {
      Swal.fire('Cannot delete column', 'A table must keep at least one column.', 'info');
      return;
    }

    table.querySelectorAll('tr').forEach((tableRow) => {
      const currentRow = tableRow as HTMLTableRowElement;
      if (currentRow.cells[cellIndex]) {
        currentRow.deleteCell(cellIndex);
      }
    });

    this.onEditorInput();
  }

  async insertTable(): Promise<void> {
    const result = await Swal.fire({
      title: 'Insert Table',
      html: `
        <div style="display:grid;gap:12px;text-align:left;">
          <label style="display:grid;gap:6px;">
            <span style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#475569;">Rows</span>
            <input id="swal-table-rows" class="swal2-input" type="number" min="1" max="8" value="3" style="margin:0;width:100%;">
          </label>
          <label style="display:grid;gap:6px;">
            <span style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#475569;">Columns</span>
            <input id="swal-table-columns" class="swal2-input" type="number" min="1" max="8" value="3" style="margin:0;width:100%;">
          </label>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Insert Table',
      focusConfirm: false,
      preConfirm: () => {
        const rowsValue = Number((document.getElementById('swal-table-rows') as HTMLInputElement | null)?.value || 0);
        const columnsValue = Number((document.getElementById('swal-table-columns') as HTMLInputElement | null)?.value || 0);

        if (!rowsValue || !columnsValue || rowsValue < 1 || columnsValue < 1 || rowsValue > 8 || columnsValue > 8) {
          Swal.showValidationMessage('Rows and columns must be between 1 and 8.');
          return null;
        }

        return {
          rows: rowsValue,
          columns: columnsValue
        };
      }
    });

    if (!result.isConfirmed || !result.value) {
      return;
    }

    const safeRows = Math.max(1, Math.min(result.value.rows, 8));
    const safeColumns = Math.max(1, Math.min(result.value.columns, 8));
    const tableStyle = 'width:100%;border-collapse:collapse;table-layout:auto;border:2px solid #64748b;margin:1rem 0 1.2rem;';
    const headerCellStyle = 'border:1.5px solid #64748b;padding:0.7rem 0.8rem;min-width:5rem;vertical-align:top;background:#e2e8f0;font-weight:700;';
    const bodyCellStyle = 'border:1.5px solid #64748b;padding:0.7rem 0.8rem;min-width:5rem;vertical-align:top;background:#ffffff;';
    const cellContentStyle = 'min-width:80px;min-height:28px;resize:both;overflow:auto;display:block;outline:none;';

    const header = `<tr>${Array.from({ length: safeColumns }, (_, index) => `<th style="${headerCellStyle}"><div class="doc-table-cell" style="${cellContentStyle}" contenteditable="true">Header ${index + 1}</div></th>`).join('')}</tr>`;
    const body = Array.from({ length: Math.max(safeRows - 1, 0) }, (_, rowIndex) =>
      `<tr>${Array.from({ length: safeColumns }, (_, columnIndex) => `<td style="${bodyCellStyle}"><div class="doc-table-cell" style="${cellContentStyle}" contenteditable="true">Row ${rowIndex + 1}, Col ${columnIndex + 1}</div></td>`).join('')}</tr>`
    ).join('');

    const tableHtml = `
      <table class="doc-table" style="${tableStyle}">
        <thead>${header}</thead>
        <tbody>${body}</tbody>
      </table>
      <p></p>
    `;

    this.focusEditor();
    document.execCommand('insertHTML', false, tableHtml);
    this.onEditorInput();
  }

  folderHasFiles(folderId: number): boolean {
    return this.knowledgeDocuments.some((document) => document.folderId === folderId);
  }

  folderHasChildren(folderId: number): boolean {
    return this.folders.some((folder) => folder.parentFolderId === folderId);
  }

  folderContents(folderId: number): KnowledgeDocument[] {
    const term = this.workspaceSearch.trim().toLowerCase();
    return this.knowledgeDocuments
      .filter((document) => document.folderId === folderId)
      .filter((document) => !term || document.title.toLowerCase().includes(term))
      .sort((a, b) => (new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
  }

  toggleFolder(folder: KnowledgeFolder, event?: Event): void {
    event?.stopPropagation();
    if (this.expandedFolderIds.has(folder.id)) {
      this.expandedFolderIds.delete(folder.id);
    } else {
      this.expandedFolderIds.add(folder.id);
    }
  }

  isFolderExpanded(folderId: number): boolean {
    return this.expandedFolderIds.has(folderId);
  }

  openCreateFolderModal(): void {
    if (!this.selectedProjectId) return;

    this.dialog.open(DocumentCreateDialogComponent, {
      width: '720px',
      panelClass: ['app-modal-panel', 'document-create-panel'],
      data: {
        mode: 'folder',
        folders: this.folders.map((folder) => ({ id: folder.id, name: folder.name })),
        parentFolderId: this.currentFolderId
      }
    }).afterClosed().subscribe((payload) => {
      if (!payload) return;
      this.projectService.createProjectKnowledgeFolder(this.selectedProjectId!, payload).subscribe({
        next: (folder) => {
          this.folders = [...this.folders, folder];
          this.expandedFolderIds.add(folder.id);
          this.currentFolderId = folder.parentFolderId ?? this.currentFolderId;
          Swal.fire('Folder created', 'The workspace folder has been added.', 'success');
        },
        error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to create folder.', 'error')
      });
    });
  }

  openCreateDocumentModal(): void {
    if (!this.selectedProjectId) return;

    this.dialog.open(DocumentCreateDialogComponent, {
      width: '820px',
      panelClass: ['app-modal-panel', 'document-create-panel'],
      data: {
        mode: 'document',
        folders: this.folders.map((folder) => ({ id: folder.id, name: folder.name })),
        parentFolderId: this.currentFolderId
      }
    }).afterClosed().subscribe((payload) => {
      if (!payload) return;
      this.projectService.createProjectKnowledgeDocument(this.selectedProjectId!, payload).subscribe({
        next: (document) => {
          this.knowledgeDocuments = [document, ...this.knowledgeDocuments];
          this.currentFolderId = document.folderId ?? this.currentFolderId;
          this.openKnowledgeDocument(document);
          this.viewMode = 'workspace';
          Swal.fire('Document created', 'The new workspace document is ready for editing.', 'success');
        },
        error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to create document.', 'error')
      });
    });
  }

  saveKnowledgeDocument(): void {
    const current = this.activeKnowledgeDocument;
    if (!current) return;
    this.onEditorInput();
    if (!this.editorModel.title.trim()) {
      Swal.fire('Validation', 'Document title is required.', 'warning');
      return;
    }

    this.savingWorkspace = true;
    this.projectService.updateProjectKnowledgeDocument(current.id, {
      title: this.editorModel.title,
      documentType: this.editorModel.documentType,
      content: this.editorModel.content,
      status: this.editorModel.status,
      folderId: this.editorModel.folderId
    }).subscribe({
      next: (document) => {
        this.knowledgeDocuments = this.knowledgeDocuments.map((item) => item.id === document.id ? document : item);
        this.openKnowledgeDocument(document);
        this.savingWorkspace = false;
        Swal.fire('Saved', 'Document changes have been saved.', 'success');
      },
      error: (err) => {
        this.savingWorkspace = false;
        Swal.fire('Error', err?.error?.message || 'Failed to save document.', 'error');
      }
    });
  }

  trackByDocumentId(_: number, document: DocumentRecord): string {
    return document.id;
  }

  trackByFolderId(_: number, folder: KnowledgeFolder): number {
    return folder.id;
  }

  trackByKnowledgeDocumentId(_: number, document: KnowledgeDocument): number {
    return document.id;
  }

  private ensureSelectedDocument(): void {
    const current = this.displayedDocument;
    if (current) {
      this.openPreview(current);
      return;
    }

    const first = this.filteredDocuments[0] || this.documents[0] || null;
    if (!first) {
      this.selectedDocumentId = null;
      this.selectedDocument = null;
      this.pdfPreviewUrl = null;
      return;
    }

    this.openPreview(first);
  }

  private ensureSelectedKnowledgeDocument(): void {
    const active = this.activeKnowledgeDocument;
    if (active) {
      this.openKnowledgeDocument(active);
      return;
    }

    const first = this.visibleKnowledgeDocuments[0] || null;
    if (!first) {
      this.selectedKnowledgeDocumentId = null;
      this.selectedKnowledgeDocument = null;
      this.editorModel = { title: '', documentType: 'Document', content: '', status: 'Draft', folderId: this.currentFolderId };
      this.syncEditorSurface();
      return;
    }

    this.openKnowledgeDocument(first);
  }

  private focusEditor(): void {
    this.documentEditor?.nativeElement.focus();
  }

  private syncEditorSurface(): void {
    setTimeout(() => {
      if (!this.documentEditor) return;
      const html = this.editorModel.content?.trim()
        ? this.editorModel.content
        : '<p></p>';
      this.documentEditor.nativeElement.innerHTML = html;
    });
  }

  private bindEditorInteractions(): void {
    const editor = this.documentEditor?.nativeElement;
    if (!editor) return;

    editor.addEventListener('pointermove', this.editorPointerMoveHandler);
    editor.addEventListener('pointerdown', this.editorPointerDownHandler);
    document.addEventListener('pointermove', this.globalPointerMoveHandler);
    document.addEventListener('pointerup', this.globalPointerUpHandler);
  }

  private unbindEditorInteractions(): void {
    const editor = this.documentEditor?.nativeElement;
    if (editor) {
      editor.removeEventListener('pointermove', this.editorPointerMoveHandler);
      editor.removeEventListener('pointerdown', this.editorPointerDownHandler);
    }

    document.removeEventListener('pointermove', this.globalPointerMoveHandler);
    document.removeEventListener('pointerup', this.globalPointerUpHandler);
  }

  private handleEditorPointerMove(event: PointerEvent): void {
    if (this.resizeState) return;

    const cell = this.findTableCell(event);
    if (!cell || !this.documentEditor) {
      this.documentEditor?.nativeElement.style.removeProperty('cursor');
      return;
    }

    const directions = this.getResizeDirections(cell, event);
    this.documentEditor.nativeElement.style.cursor = this.getCursor(directions.directionX, directions.directionY);
  }

  private handleEditorPointerDown(event: PointerEvent): void {
    const cell = this.findTableCell(event);
    if (!cell) return;

    const directions = this.getResizeDirections(cell, event);
    if (!directions.directionX && !directions.directionY) return;

    event.preventDefault();
    event.stopPropagation();

    this.resizeState = {
      cell,
      directionX: directions.directionX,
      directionY: directions.directionY,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: cell.offsetWidth,
      startHeight: cell.offsetHeight
    };
  }

  private handleGlobalPointerMove(event: PointerEvent): void {
    if (!this.resizeState) return;

    const deltaX = event.clientX - this.resizeState.startX;
    const deltaY = event.clientY - this.resizeState.startY;
    const widthDelta = this.resizeState.directionX === 'left' ? -deltaX : deltaX;
    const heightDelta = this.resizeState.directionY === 'top' ? -deltaY : deltaY;

    if (this.resizeState.directionX) {
      const nextWidth = Math.max(80, this.resizeState.startWidth + widthDelta);
      this.resizeState.cell.style.width = `${nextWidth}px`;
      this.resizeState.cell.style.minWidth = `${nextWidth}px`;
    }

    if (this.resizeState.directionY) {
      const nextHeight = Math.max(40, this.resizeState.startHeight + heightDelta);
      this.resizeState.cell.style.height = `${nextHeight}px`;
      this.resizeState.cell.style.minHeight = `${nextHeight}px`;
    }
  }

  private stopResize(): void {
    this.resizeState = null;
    this.documentEditor?.nativeElement.style.removeProperty('cursor');
    this.onEditorInput();
  }

  private findTableCell(event: PointerEvent): HTMLElement | null {
    const target = event.target as HTMLElement | null;
    if (!target) return null;
    return target.closest('td, th');
  }

  private getSelectedTableCell(): HTMLTableCellElement | null {
    const selection = window.getSelection();
    const anchorNode = selection?.anchorNode;
    const target = anchorNode instanceof HTMLElement
      ? anchorNode
      : anchorNode?.parentElement || null;

    return (target?.closest('td, th') as HTMLTableCellElement | null) || null;
  }

  private getHeaderCellStyle(): string {
    return 'border:1.5px solid #64748b;padding:0.7rem 0.8rem;min-width:5rem;vertical-align:top;background:#e2e8f0;font-weight:700;';
  }

  private getBodyCellStyle(): string {
    return 'border:1.5px solid #64748b;padding:0.7rem 0.8rem;min-width:5rem;vertical-align:top;background:#ffffff;';
  }

  private getCellContentStyle(): string {
    return 'width:100%;min-width:100%;min-height:28px;display:block;overflow:hidden;outline:none;white-space:normal;box-sizing:border-box;';
  }

  private getResizeDirections(cell: HTMLElement, event: PointerEvent): { directionX: 'left' | 'right' | null; directionY: 'top' | 'bottom' | null } {
    const rect = cell.getBoundingClientRect();
    const threshold = 10;
    const nearLeft = event.clientX - rect.left <= threshold;
    const nearRight = rect.right - event.clientX <= threshold;
    const nearTop = event.clientY - rect.top <= threshold;
    const nearBottom = rect.bottom - event.clientY <= threshold;

    return {
      directionX: nearLeft ? 'left' : nearRight ? 'right' : null,
      directionY: nearTop ? 'top' : nearBottom ? 'bottom' : null
    };
  }

  private getCursor(directionX: 'left' | 'right' | null, directionY: 'top' | 'bottom' | null): string {
    if (directionX && directionY) {
      if ((directionX === 'left' && directionY === 'top') || (directionX === 'right' && directionY === 'bottom')) {
        return 'nwse-resize';
      }
      return 'nesw-resize';
    }

    if (directionX) return 'ew-resize';
    if (directionY) return 'ns-resize';
    return 'text';
  }

  private getFileTypeLabel(file: any): string {
    const contentType = String(file?.contentType || '').toLowerCase();
    const name = String(file?.fileName || file?.name || file?.url || '').toLowerCase();
    if (contentType.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg)$/.test(name)) return 'Image';
    if (contentType === 'application/pdf' || /\.pdf$/.test(name)) return 'PDF';
    if (contentType.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/.test(name)) return 'Video';
    if (contentType.startsWith('audio/') || /\.(mp3|wav|ogg|m4a)$/.test(name)) return 'Audio';
    return 'File';
  }
}
